import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ownerApi } from './ownerClient'
import type {
  OwnerCategory,
  OwnerCategoryPayload,
  OwnerProduct,
  OwnerProductPayload,
} from '../types'

/**
 * Owner-side catalog management over the staff-gated Phase 2 endpoints (`/api/categories`,
 * `/api/products`). The tenant is ambient (staff JWT + `@TenantId`); these never send a
 * tenant id. List queries are keyed under `['catalog', ...]` so a mutation can invalidate both.
 */

async function fetchCategories(): Promise<OwnerCategory[]> {
  const { data } = await ownerApi.get<OwnerCategory[]>('/categories')
  return data
}

async function fetchProducts(): Promise<OwnerProduct[]> {
  const { data } = await ownerApi.get<OwnerProduct[]>('/products')
  return data
}

export function useOwnerCategories(enabled: boolean) {
  return useQuery({ queryKey: ['catalog', 'categories'], queryFn: fetchCategories, enabled })
}

export function useOwnerProducts(enabled: boolean) {
  return useQuery({ queryKey: ['catalog', 'products'], queryFn: fetchProducts, enabled })
}

/** Invalidates the whole catalog cache after any mutation so lists re-read from the server. */
function useInvalidateCatalog() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['catalog'] })
}

export function useSaveCategory() {
  const invalidate = useInvalidateCatalog()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: OwnerCategoryPayload }) => {
      const { data } = id
        ? await ownerApi.put<OwnerCategory>(`/categories/${id}`, payload)
        : await ownerApi.post<OwnerCategory>('/categories', payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteCategory() {
  const invalidate = useInvalidateCatalog()
  return useMutation({
    mutationFn: async (id: string) => {
      await ownerApi.delete(`/categories/${id}`)
    },
    onSuccess: invalidate,
  })
}

export function useSaveProduct() {
  const invalidate = useInvalidateCatalog()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: OwnerProductPayload }) => {
      const { data } = id
        ? await ownerApi.put<OwnerProduct>(`/products/${id}`, payload)
        : await ownerApi.post<OwnerProduct>('/products', payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useSetProductAvailability() {
  const invalidate = useInvalidateCatalog()
  return useMutation({
    mutationFn: async ({ id, available }: { id: string; available: boolean }) => {
      const { data } = await ownerApi.patch<OwnerProduct>(`/products/${id}/availability`, { available })
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteProduct() {
  const invalidate = useInvalidateCatalog()
  return useMutation({
    mutationFn: async (id: string) => {
      await ownerApi.delete(`/products/${id}`)
    },
    onSuccess: invalidate,
  })
}
